/**
 * Key chords: the canonical text form every binding is stored in.
 *
 * A chord is `[Ctrl+][Alt+][Shift+]Key`, modifiers always in that order.
 * "Ctrl" means the platform command modifier (Ctrl, or Cmd on macOS), matching
 * how the editor has always treated `ctrlKey || metaKey`. Keys use stable
 * names: `A`-`Z`, `0`-`9`, `F1`-`F24`, `Space`, `Tab`, `Enter`, `Escape`,
 * `Delete`, `Backspace`, `Home`, `End`, `PageUp`, `PageDown`, `Up`, `Down`,
 * `Left`, `Right`, `Insert`, `Num0`-`Num9`, and punctuation by character
 * (`` ` `` `,` `.` `/` `-` `=` `[` `]` `;` `'` `\`).
 */

export interface ChordEvent {
  key: string;
  code?: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

const MODIFIER_KEYS = new Set(['Control', 'Shift', 'Alt', 'Meta', 'AltGraph', 'OS', 'CapsLock']);

const CODE_NAMES: Record<string, string> = {
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Space: 'Space',
  Tab: 'Tab',
  Enter: 'Enter',
  NumpadEnter: 'Enter',
  Escape: 'Escape',
  Delete: 'Delete',
  Backspace: 'Backspace',
  Insert: 'Insert',
  Home: 'Home',
  End: 'End',
  PageUp: 'PageUp',
  PageDown: 'PageDown',
  ArrowUp: 'Up',
  ArrowDown: 'Down',
  ArrowLeft: 'Left',
  ArrowRight: 'Right',
  NumpadAdd: 'Num+',
  NumpadSubtract: 'Num-',
  NumpadMultiply: 'Num*',
  NumpadDivide: 'Num/',
  NumpadDecimal: 'Num.',
};

const KEY_ALIASES: Record<string, string> = {
  ' ': 'Space',
  spacebar: 'Space',
  space: 'Space',
  esc: 'Escape',
  escape: 'Escape',
  del: 'Delete',
  delete: 'Delete',
  backspace: 'Backspace',
  return: 'Enter',
  enter: 'Enter',
  tab: 'Tab',
  arrowup: 'Up',
  arrowdown: 'Down',
  arrowleft: 'Left',
  arrowright: 'Right',
  up: 'Up',
  down: 'Down',
  left: 'Left',
  right: 'Right',
  home: 'Home',
  end: 'End',
  pageup: 'PageUp',
  pagedown: 'PageDown',
  insert: 'Insert',
  backquote: '`',
  grave: '`',
  plus: '=',
  comma: ',',
  period: '.',
};

/** Canonical key name for a single key token, or null when unrecognised. */
export function normalizeKey(token: string): string | null {
  const raw = token.trim();
  if (!raw) return null;
  if (raw.length === 1) {
    if (/[a-z]/i.test(raw)) return raw.toUpperCase();
    if (/[0-9`\-=[\]\\;',./]/.test(raw)) return raw;
    if (raw === ' ') return 'Space';
    return null;
  }
  const lower = raw.toLowerCase();
  if (KEY_ALIASES[lower]) return KEY_ALIASES[lower]!;
  const fn = /^f([1-9]|1[0-9]|2[0-4])$/i.exec(raw);
  if (fn) return `F${fn[1]}`;
  const num = /^num(?:pad)?([0-9+\-*/.])$/i.exec(raw);
  if (num) return `Num${num[1]}`;
  return null;
}

/**
 * Parse a chord written by a person or stored in a profile
 * (`ctrl+shift+z`, `Cmd+K`, `Shift+\``) into canonical form, or null.
 */
export function parseChord(text: string): string | null {
  if (!text) return null;
  // Split on '+' but keep a literal trailing '+' key ("Ctrl++" is Ctrl and '=').
  const parts = text.endsWith('++') ? [...text.slice(0, -2).split('+'), '='] : text.split('+');
  let ctrl = false;
  let alt = false;
  let shift = false;
  let key: string | null = null;
  for (const part of parts) {
    const p = part.trim().toLowerCase();
    if (p === 'ctrl' || p === 'control' || p === 'cmd' || p === 'command' || p === 'meta' || p === 'mod' || p === 'cmdorctrl') ctrl = true;
    else if (p === 'alt' || p === 'option' || p === 'opt') alt = true;
    else if (p === 'shift') shift = true;
    else {
      if (key !== null) return null;
      key = normalizeKey(part);
      if (key === null) return null;
    }
  }
  if (key === null) return null;
  return compose(ctrl, alt, shift, key);
}

function compose(ctrl: boolean, alt: boolean, shift: boolean, key: string): string {
  return `${ctrl ? 'Ctrl+' : ''}${alt ? 'Alt+' : ''}${shift ? 'Shift+' : ''}${key}`;
}

/**
 * The chord a keyboard event represents, or null for a bare modifier press.
 * Letters follow the active keyboard layout (`key`), everything else the
 * physical key (`code`) so Shift+1 stays "Shift+1" rather than "!".
 */
export function eventToChord(event: ChordEvent): string | null {
  if (MODIFIER_KEYS.has(event.key)) return null;
  let key: string | null = null;
  if (event.key.length === 1 && /[a-z]/i.test(event.key)) key = event.key.toUpperCase();
  else if (event.code) {
    const digit = /^(?:Digit)([0-9])$/.exec(event.code);
    const numpad = /^Numpad([0-9])$/.exec(event.code);
    const letter = /^Key([A-Z])$/.exec(event.code);
    if (digit) key = digit[1]!;
    else if (numpad) key = `Num${numpad[1]}`;
    else if (letter) key = letter[1]!;
    else if (CODE_NAMES[event.code]) key = CODE_NAMES[event.code]!;
    else if (/^F([1-9]|1[0-9]|2[0-4])$/.test(event.code)) key = event.code;
  }
  key ??= normalizeKey(event.key);
  if (!key) return null;
  return compose(event.ctrlKey || event.metaKey, event.altKey, event.shiftKey, key);
}

/** True when a chord has no Ctrl/Alt and is not a function key (it types or plays). */
export function isPlainChord(chord: string): boolean {
  if (chord.startsWith('Ctrl+') || chord.includes('Alt+')) return false;
  const key = chord.replace(/^Shift\+/, '');
  return !/^F([1-9]|1[0-9]|2[0-4])$/.test(key);
}

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');

/** Human label for a chord: "Ctrl+Shift+Z", or "⌘⇧Z" on macOS. */
export function formatChord(chord: string): string {
  if (!MAC) return chord;
  return chord
    .replace('Ctrl+', '⌘')
    .replace('Alt+', '⌥')
    .replace('Shift+', '⇧');
}

/** Electron accelerator for a chord ("CmdOrCtrl+Shift+Z"), or null if Electron can't show it. */
export function toAccelerator(chord: string): string | null {
  const parts = chord.split('+');
  const key = chord.endsWith('++') ? '+' : parts.at(-1)!;
  const mods = parts.slice(0, -1).map((m) => (m === 'Ctrl' ? 'CmdOrCtrl' : m));
  const map: Record<string, string> = { Up: 'Up', Down: 'Down', Left: 'Left', Right: 'Right', Escape: 'Esc' };
  const name = map[key] ?? (key.startsWith('Num') && key.length === 4 ? `num${key.slice(3)}` : key);
  if (!name) return null;
  return [...mods, name].join('+');
}
